import React, {
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useHistory } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUp } from '@fortawesome/pro-regular-svg-icons';
import { last } from 'lodash';
import gql from 'graphql-tag';

import { Alert, EllipsedLine } from '../../ui';
import { SimpleForm, TextInput } from '../../forms';

import { BEDROCK_TOOL_LOADING_TEXT_BY_NAME } from '../../constants';
import agentLogRowFragment from '../../fragments/agentLogRowFragment';
import conversationFragment from '../../fragments/conversationFragment';

import withData from '../../decorators/withData';
import withRecentView from '../../decorators/withRecentView';
import withRouter from '../../decorators/withRouter';

import { RightPanelProvider } from '../../contexts/RightPanelContext';

import { track } from '../../services/Analytics';

import useLocation from '../../hooks/useLocation';
import useToken from '../../hooks/useToken';

import AccountLink from '../links/AccountLink';
import Actions from '../Actions';
import AgentLoadingIndicator from '../AgentLoadingIndicator';
import AgentLogRows from '../AgentLogRows';
import Conversation from '../Conversation';
import { DataContext } from '../Data';
import ErrorMessage from '../ErrorMessage';
import FixedLayout from '../FixedLayout';
import InfoPanel from '../InfoPanel';
import MainDisplayObjectIcon from '../icons/MainDisplayObjectIcon';
import StarControl from '../StarControl';
import Path from '../Path';

import composeWithNonDialogComponents from '../../utils/composeWithNonDialogComponents';
import fqdn from '../../utils/fqdn';
import getMainDisplayObjectName from '../../utils/getMainDisplayObjectName';
import getSegmentMainDisplayObjectVariables from '../../utils/getSegmentMainDisplayObjectVariables';

const SEGMENT_PAGE_CTXT = 'ConversationPage';

const QUERY_CONVERSATION = gql`
  query conversation($id: DokkioID!) {
    conversation(id: $id) {
      ...conversationFragment
      agentLogRows {
        ...agentLogRowFragment
      }
    }
  }
  ${agentLogRowFragment}
  ${conversationFragment}
`;

const getToolLoadingText = (agentLogRows) => {
  for (let rowIndex = agentLogRows.length - 1; rowIndex >= 0; rowIndex -= 1) {
    const parts = agentLogRows[rowIndex].parts.filter(Boolean);
    for (let partIndex = parts.length - 1; partIndex >= 0; partIndex -= 1) {
      const part = parts[partIndex];
      if (part.type === 'tool_call') {
        return BEDROCK_TOOL_LOADING_TEXT_BY_NAME[part.name] || part.name;
      }
      if (part.type === 'text' || part.type === 'tool_result') {
        return null;
      }
    }
  }
  return null;
};

const MAX_TOKENS_BEFORE_DISABLE_INPUT_COUNT = 200000;

export const ConversationView = () => {
  const { push } = useHistory();
  const { query: { active: activeString } } = useLocation();
  const {
    conversation,
    conversation: {
      accountID,
      id,
      remoteConversationID,
      conversationUserPrompts,
      agentLogRows,
    },
    refetchConversation,
    integrations,
  } = useContext(DataContext);

  const [token] = useToken();
  const scrollDivRef = useRef(null);
  const hasTrackedOverConvLimitRef = useRef(false);
  const lastTotalTokensRef = useRef(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isOverMaxTokens, setIsOverMaxTokens] = useState(false);
  const [currentAgentLogRows, setCurrentAgentLogRows] = useState([]);

  const isActive = activeString === 'true';
  const lastConversationUserPrompt = last(conversationUserPrompts) || {};
  const isStillLoadingText = integrations.some(({ textLoadingCount }) => textLoadingCount > 0);

  useEffect(() => {
    hasTrackedOverConvLimitRef.current = false;
    lastTotalTokensRef.current = null;
    track('Conversation Viewed', getSegmentMainDisplayObjectVariables({
      mainDisplayObject: conversation,
    }));
  }, [id]);

  useEffect(() => {
    window.scrollDivRef = scrollDivRef.current;
    if (scrollDivRef.current && isActive) {
      scrollDivRef.current.scrollIntoView();
    }
  }, [currentAgentLogRows, scrollDivRef.current]);

  const onContinueConversation = async ({ prompt }, { reset = () => null } = {}) => {
    track('Chat UserPrompt', {
      Prompt: prompt,
    });
    reset();
    const newCurrentAgentLogRows = [{
      role: 'user',
      parts: [{
        type: 'text',
        text: prompt,
      }],
    }];
    setCurrentAgentLogRows(newCurrentAgentLogRows);
    setIsSubmitting(true);
    const trackOverConvLimit = () => {
      if (hasTrackedOverConvLimitRef.current) {
        return;
      }
      hasTrackedOverConvLimitRef.current = true;
      // Tool results also arrive as user-role rows, so only count text
      // prompts; the new turn isn't in agentLogRows yet
      const promptCount = agentLogRows.filter(({ role, parts }) => (
        role === 'user' && parts.some(part => part.type === 'text')
      )).length + 1;
      track('Chat OverConvLimit', {
        PromptCount: promptCount,
        TokenCount: lastTotalTokensRef.current,
      });
    };
    const sseURL = `https://${fqdn('api')}/v2/mcp.chat`;
    const response = await fetch(sseURL, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token.accessToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        prompt,
        remoteConversationID,
      }),
    });
    if (!response.ok) {
      const body = await response.json();
      setIsSubmitting(false);
      throw body.error;
    } else {
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let done;
      let value;
      let newAgentLogRow;
      do {
        ({ done, value } = await reader.read());
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = JSON.parse(line.substr(6));
            const {
              agentLogRowStart,
              agentLogRowPartStart,
              agentLogRowPartDelta,
              agentLogRowPartStop,
              error: streamError,
              usage,
            } = data;
            if (agentLogRowStart) {
              newAgentLogRow = { role: agentLogRowStart.role, parts: [] };
              newCurrentAgentLogRows.push(newAgentLogRow);
            } else if (agentLogRowPartStart) {
              newAgentLogRow.parts[agentLogRowPartStart.partIndex] = agentLogRowPartStart.part;
            } else if (agentLogRowPartDelta) {
              const { partIndex, text } = agentLogRowPartDelta;
              if (!newAgentLogRow.parts[partIndex]) {
                newAgentLogRow.parts[partIndex] = { type: 'text', text: '' };
              }
              const part = newAgentLogRow.parts[partIndex];
              if (part.type === 'text') {
                part.text += text;
              } else {
                part.input += text;
              }
            } else if (agentLogRowPartStop) {
              newAgentLogRow.parts[agentLogRowPartStop.partIndex] = agentLogRowPartStop.part;
            } else if (streamError) {
              setIsSubmitting(false);
              if (streamError.message.includes('Input is too long for requested model')) {
                trackOverConvLimit();
                setIsOverMaxTokens(true);
              } else {
                throw streamError.message;
              }
            } else if (usage) {
              const { totalTokens } = usage;
              lastTotalTokensRef.current = totalTokens;
              if (totalTokens >= MAX_TOKENS_BEFORE_DISABLE_INPUT_COUNT) {
                trackOverConvLimit();
                setIsOverMaxTokens(true);
              }
            }
          }
        }
        setCurrentAgentLogRows([...newCurrentAgentLogRows]);
      } while (!done);
    }
    await refetchConversation();
    setCurrentAgentLogRows([]);
    setIsSubmitting(false);
  };

  useEffect(() => {
    if (!agentLogRows.length) {
      onContinueConversation({ prompt: lastConversationUserPrompt.prompt });
    }
  }, [conversationUserPrompts.length]);

  const renderHeaderActions = () => (
    <Actions
      actionContext="pageHeader"
      buttonActions={[{
        action: 'open',
      }]}
      dropdownActions={[{
        action: 'createTask',
      }, {
        action: 'star',
      }, {
        action: 'rename',
      }, {
        action: 'setDokkioTags',
      }, {
        action: 'delete',
        onSuccess: () => push(`/a/${accountID}/conversations`),
      }]}
      onSuccess={refetchConversation}
      segmentPageCtxt={SEGMENT_PAGE_CTXT}
      targets={[conversation]}
    />
  );

  return (
    <RightPanelProvider>
      <FixedLayout
        renderPic={picPx => (
          <MainDisplayObjectIcon
            iconPx={picPx}
            mainDisplayObject={conversation}
          />
        )}
        renderActions={renderHeaderActions}
        renderTitle={() => (
          <React.Fragment>
            <EllipsedLine>
              {getMainDisplayObjectName({ mainDisplayObject: conversation })}
            </EllipsedLine>
            <StarControl object={conversation} segmentPageCtxt={SEGMENT_PAGE_CTXT} />
          </React.Fragment>
        )}
        renderDescription={() => (
          <Path
            mainDisplayObject={conversation}
            segmentPageCtxt={SEGMENT_PAGE_CTXT}
          />
        )}
        renderPanel={() => (
          <InfoPanel
            mainDisplayObject={conversation}
            onObjectsChanged={refetchConversation}
            segmentPageCtxt={SEGMENT_PAGE_CTXT}
          />
        )}
      >
        <div className="relative flex flex-col flex-1 h-full w-full max-w-2xl mx-auto p-4 space-y-2.5">
          {!conversation.isTextLoadingComplete && (
            <Alert alertStyle="info">
              This response may be incomplete. Your content was still
              being scanned and indexed when it was generated.
              {!isStillLoadingText && (
                <React.Fragment>
                  {' '}
                  You can
                  {' '}
                  <AccountLink to="/conversations">try your question again</AccountLink>
                </React.Fragment>
              )}
            </Alert>
          )}
          <Conversation
            allowRating={isActive}
            agentLogRows={agentLogRows}
            conversation={conversation}
            segmentPageCtxt={SEGMENT_PAGE_CTXT}
          />
          <AgentLogRows
            agentLogRows={currentAgentLogRows}
            segmentPageCtxt={SEGMENT_PAGE_CTXT}
            isSubmitting={isSubmitting}
          />
          <SimpleForm
            className="relative sticky bottom-0 w-full flex flex-col pb-4 bg-white dark:bg-black space-y-2.5"
            initialValueByFieldName={{ prompt: '' }}
            onSubmit={onContinueConversation}
          >
            {({
              formError,
              getFieldValue,
              onSubmit,
            }) => (
              <React.Fragment>
                {isSubmitting && (
                  <AgentLoadingIndicator text={getToolLoadingText(currentAgentLogRows)} />
                )}
                <ErrorMessage errors={[formError]} />
                {isOverMaxTokens && !isSubmitting && (
                  <Alert alertStyle="info">
                    The conversation is too long to continue,
                    {' '}
                    <AccountLink to="/conversations">please start a new one</AccountLink>
                  </Alert>
                )}
                <div ref={scrollDivRef} />
                {isActive && !isOverMaxTokens && (
                  <div className="relative">
                    <button
                      className="w-8 h-8 rounded-lg bg-dokkio-yellow absolute bottom-4 right-2 disabled:opacity-50 cursor-pointer"
                      type="submit"
                      disabled={!getFieldValue('prompt').trim()}
                    >
                      <FontAwesomeIcon className="text-white dark:text-black" icon={faArrowUp} />
                    </button>
                    <TextInput
                      name="prompt"
                      placeholder="Reply to Dokkio"
                      type="textarea"
                      disabled={isSubmitting || isOverMaxTokens}
                      onKeyDown={async (e) => {
                        if (e.key === 'Enter' && !e.shiftKey && e.target.value.trim()) {
                          await onSubmit(e, onContinueConversation, () => null);
                        }
                      }}
                    />
                  </div>
                )}
              </React.Fragment>
            )}
          </SimpleForm>
        </div>
      </FixedLayout>
    </RightPanelProvider>
  );
};

export default composeWithNonDialogComponents(
  withRouter,
  withRecentView,
  withData(({ match: { params: { conversationID } } }) => ({
    queries: [{
      query: QUERY_CONVERSATION,
      variables: { id: conversationID },
    }],
    showLoader: 'initial-load-only',
  })),
)(ConversationView);
